import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EPaginationQueries, ROWS_PER_PAGE_OPTIONS } from '@agco-onedigital/ecosystem-lib-web-components';

const getTotalPages = (totalItems: number, rowsPerPage: number) => Math.ceil(totalItems / rowsPerPage);

const getPage = (pageString: string | null, totalItems: number, rowsPerPage: number) => {
  // Parse page from URL (1-indexed) or default to 0
  const urlPage = Number(pageString);
  if (!pageString || Number.isNaN(urlPage) || urlPage < 1) {
    return 0;
  }

  // Convert from 1-indexed (URL) to 0-indexed (internal)
  const zeroIndexedPage = urlPage - 1;

  // Clamp to valid range
  const totalPages = getTotalPages(totalItems, rowsPerPage);
  if (totalPages === 0) {
    return 0;
  }
  if (zeroIndexedPage >= totalPages) {
    return totalPages - 1;
  }

  return zeroIndexedPage;
};

const getRowsPerPage = (rowsPerPageString: string | null) => {
  const queryOption = ROWS_PER_PAGE_OPTIONS.find(option => option.label === rowsPerPageString);
  if (queryOption) {
    return queryOption.value;
  }
  return 10;
};

export const usePagination = <T>(items: T[]) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive values directly from URL params
  const rowsPerPage = getRowsPerPage(searchParams.get(EPaginationQueries.ROWS_PER_PAGE));
  const page = getPage(searchParams.get(EPaginationQueries.PAGE), items.length, rowsPerPage);
  const slicedItems = items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Scroll to top when page changes via URL (handles browser back/forward navigation)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  // Auto-correct page if it's out of bounds
  useEffect(() => {
    if (items.length) {
      const totalPages = getTotalPages(items.length, rowsPerPage);
      if (totalPages > 0 && page >= totalPages) {
        setSearchParams(prevParams => {
          const newParams = new URLSearchParams(prevParams);
          const correctedPage = totalPages - 1;
          if (correctedPage === 0) {
            newParams.delete(EPaginationQueries.PAGE);
          } else {
            newParams.set(EPaginationQueries.PAGE, String(correctedPage + 1));
          }
          return newParams;
        });
      }
    }
  }, [items.length, rowsPerPage, page, setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams);
        const pageIndex = newPage - 1;

        if (pageIndex === 0) {
          newParams.delete(EPaginationQueries.PAGE);
        } else {
          newParams.set(EPaginationQueries.PAGE, String(newPage));
        }

        return newParams;
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams],
  );

  const handleRowPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams);

        // Reset to page 1 when changing rows per page
        newParams.delete(EPaginationQueries.PAGE);

        if (newRowsPerPage === 10) {
          newParams.delete(EPaginationQueries.ROWS_PER_PAGE);
        } else {
          newParams.set(EPaginationQueries.ROWS_PER_PAGE, String(newRowsPerPage));
        }

        return newParams;
      });
    },
    [setSearchParams],
  );

  return {
    rowsPerPage,
    page,
    items: slicedItems,
    onPageChange: handlePageChange,
    onRowPerPageChange: handleRowPerPageChange,
  };
};
